import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MenuItemType } from '@next-nx/utils/types'
import { Button, WImage } from '@next-nx/shared-ui/atoms'
import styles from './CircleBox.module.sass'
import ArrowRight from '../../atoms/Icons/ArrowRight'
import { useTealium, useTrans, useWindowDimensions } from '@next-nx/hooks'
import ChevronDown from '../../atoms/Icons/ChevronDown'
import ChevronUp from '../../atoms/Icons/ChevronUp'
import { getTimestamp } from '@next-nx/utils'

interface Props {
  item: MenuItemType
  size?: 'sm' | 'md'
  variant?: 'vertical' | 'horizontal'
  animationVariant?: 'vertical' | 'horizontal'
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  handleTracking?: (ty?: string, c?: string, t?: string, cta?: string) => void
  className?: string
  ctaClassName?: string
  editMode?: boolean
}

const CircleBox = (props: Props) => {
  const {
    item = undefined,
    size = 'md',
    variant = 'vertical',
    animationVariant = 'vertical',
    onClick = undefined,
    handleTracking = () => undefined,
    className = '',
    ctaClassName = '',
    editMode = false,
  } = props

  const t = useTrans()
  const { linkDataLayer } = useTealium()
  const { width } = useWindowDimensions()
  const descriptionRef = useRef(null)
  const [readMoreOpen, setReadMoreOpen] = useState(false)
  const [showReadMore, setShowReadMore] = useState(false)

  const customHandleTracking = (action_type: string = '', interazione: string = '') => {
    const action_timestamp = getTimestamp()
    let dlObj = {}

    switch (interazione) {
      case 'riconsegna_veicolo':
        dlObj = {
          virtual: 'action',
          action_name: 'navigazione',
          action_tech: 'click',
          action_type: action_type.replace(/\’|\s+/g, '_').toLowerCase(),
          action_timestamp,
          action_effect: 'interazione_componente_riconsegna_veicolo',
        }
        break
      default:
        break
    }

    Object.keys(dlObj).length > 0 && linkDataLayer(dlObj)
  }

  useEffect(() => {
    const lineHeight = 24
    const maxLines = width < 768 ? 4 : 5
    if (descriptionRef?.current?.clientHeight) {
      setShowReadMore(descriptionRef?.current?.clientHeight > lineHeight * maxLines)
    }
  }, [width, descriptionRef?.current?.clientHeight])

  const imageBox = useMemo(
    () =>
      item && (
        <WImage
          src={item.image}
          alt={item.name}
          title={item.name}
          maxWidth={262}
          maxHeight={131}
          layout="fill"
          objectFit="contain"
        />
      ),
    [item?.image]
  )

  return item ? (
    <div
      className={`${styles.root} ${!!item.image ? styles['root--image'] : ''} ${
        styles[`root--${size}`]
      } ${!!item.description ? '' : styles['root--no-description']} ${
        styles[`root--animation-${animationVariant}`]
      } ${className}`}
    >
      <div className={`${styles.circleContainer} ${styles[`circleContainer--${variant}`]}`}>
        <div className={styles.content}>
          {!!item.name &&
            (editMode || typeof item.name !== 'string' ? (
              <div className={styles.title}>{item.name}</div>
            ) : (
              <p className={styles.title}>{item.name}</p>
            ))}
          {!!item.description &&
            (variant === 'vertical' ? (
              <>
                <div
                  className={`${styles.descriptionBox} ${
                    showReadMore ? styles['descriptionBox--overflow'] : ''
                  }`}
                  style={{
                    height:
                      showReadMore && !editMode
                        ? descriptionRef?.current && readMoreOpen
                          ? descriptionRef.current.clientHeight
                          : 0
                        : undefined,
                  }}
                >
                  {!!item.description &&
                    (editMode || typeof item.description !== 'string' ? (
                      <div className={styles.description} ref={descriptionRef}>
                        {item.description}
                      </div>
                    ) : (
                      <div
                        className={styles.description}
                        dangerouslySetInnerHTML={{ __html: item.description }}
                        ref={descriptionRef}
                      />
                    ))}
                </div>
                {showReadMore && !editMode && (
                  <div className={styles.readMoreBox}>
                    <Button
                      label={t(readMoreOpen ? 'Chiudi' : 'Leggi di più')}
                      icon={readMoreOpen ? <ChevronUp /> : <ChevronDown />}
                      iconPos="right"
                      variant="ghost"
                      color="white"
                      onClick={() => setReadMoreOpen(!readMoreOpen)}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={styles.descriptionBox}>
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                </div>
              </>
            ))}
          {variant === 'vertical' && (!!item.url || onClick) && !!item.ctaLabel && (
            <div className={styles.ctaBox}>
              <Button
                href={item.url}
                onClick={(e) => {
                  onClick && onClick(e)
                  customHandleTracking(item.ctaLabel, item.key)
                }}
                newPage={item.blank}
                label={item.ctaLabel}
                handleTracking={handleTracking}
                name={item.name}
                variant="ghost"
                color="white"
                icon={<ArrowRight />}
                iconPos="right"
                file={item.file}
                className={ctaClassName}
              />
            </div>
          )}
        </div>
        {variant === 'horizontal' && (!!item.url || onClick) && !!item.ctaLabel && (
          <div className={`${styles.ctaBox} ${styles['ctaBox--horizontal']}`}>
            <Button
              href={item.url}
              onClick={(e) => {
                onClick && onClick(e)
                customHandleTracking(item.ctaLabel, item.key)
              }}
              newPage={item.blank}
              label={item.ctaLabel}
              handleTracking={handleTracking}
              name={item.name}
              variant="primary"
              file={item.file}
              className={ctaClassName}
            />
          </div>
        )}
        {!(!!item.url || onClick) && !item?.ctaLabel && item?.children?.edges?.length && (
          <div className={`${styles.ctaBoxes}`}>
            {item?.children?.edges?.map(
              (
                x,
                i // max 2 children
              ) => (
                <div className={`${styles.ctaBox}`}>
                  <Button
                    href={x.node.url}
                    onClick={() => {
                      customHandleTracking(x.node.ctaLabel, x.node.key)
                    }}
                    newPage={x.node.blank}
                    label={x.node.ctaLabel}
                    handleTracking={handleTracking}
                    name={x.node.name}
                    variant="ghost"
                    color="white"
                    icon={<ArrowRight />}
                    iconPos="right"
                    file={x.node.file}
                    className={ctaClassName}
                  />
                </div>
              )
            )}
          </div>
        )}
        {variant === 'horizontal' &&
          !(!!item.url || onClick) &&
          !item?.ctaLabel &&
          item?.children?.edges?.length && (
            <div className={`${styles.ctaBoxes}`}>
              {item?.children?.edges?.map((x) => (
                <div className={`${styles.ctaBox} ${styles['ctaBox--horizontal']}`}>
                  <Button
                    href={x.node.url}
                    onClick={() => {
                      customHandleTracking(x.node.ctaLabel, x.node.key)
                    }}
                    newPage={x.node.blank}
                    label={x.node.ctaLabel}
                    handleTracking={handleTracking}
                    name={x.node.name}
                    variant="primary"
                    file={x.node.file}
                    className={ctaClassName}
                  />
                </div>
              ))}
            </div>
          )}
      </div>
      {!!item.image && <div className={styles.imageBox}>{imageBox}</div>}
    </div>
  ) : (
    <></>
  )
}

export default CircleBox
